import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaTimes, FaMobileAlt, FaApple, FaAndroid, FaShareAlt, FaPlus, FaEllipsisV, FaHome } from 'react-icons/fa';
import { theme } from '../../styles/theme';

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const BannerContainer = styled.div`
  position: fixed;
  top: ${props => props.$position === 'top' ? '0' : 'auto'};
  bottom: ${props => props.$position === 'bottom' ? '0' : 'auto'};
  left: 0;
  right: 0;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.95) 0%, rgba(255, 140, 0, 0.95) 100%);
  backdrop-filter: blur(10px);
  padding: ${theme.spacing.lg};
  box-shadow: 0 4px 20px rgba(255, 215, 0, 0.3);
  z-index: 1000;
  animation: ${slideDown} 0.5s ease-out;
  border-bottom: ${props => props.$position === 'top' ? '2px solid rgba(0, 0, 0, 0.1)' : 'none'};
  border-top: ${props => props.$position === 'bottom' ? '2px solid rgba(0, 0, 0, 0.1)' : 'none'};

  @media (max-width: 768px) {
    padding: ${theme.spacing.md};
  }
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const BannerText = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  color: #000;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
  }
`;

const IconContainer = styled.div`
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const TextContent = styled.div`
  flex: 1;
`;

const BannerTitle = styled.h3`
  margin: 0 0 ${theme.spacing.xs} 0;
  font-size: ${theme.typography.fontSizes.lg};
  font-weight: ${theme.typography.fontWeights.bold};
  color: #000;

  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSizes.md};
  }
`;

const BannerDescription = styled.p`
  margin: 0;
  font-size: ${theme.typography.fontSizes.sm};
  color: rgba(0, 0, 0, 0.8);
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSizes.xs};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const InstallButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  background: #000;
  color: #FFD700;
  border: none;
  border-radius: ${theme.borderRadius.medium};
  font-weight: ${theme.typography.fontWeights.semibold};
  font-size: ${theme.typography.fontSizes.sm};
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    background: #1a1a1a;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-size: ${theme.typography.fontSizes.xs};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #000;
  font-size: 1.5rem;
  cursor: pointer;
  padding: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }

  @media (max-width: 768px) {
    position: absolute;
    top: ${theme.spacing.sm};
    right: ${theme.spacing.sm};
  }
`;

const InstructionsModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${theme.spacing.lg};
  animation: ${slideDown} 0.3s ease-out;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.xxl};
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(255, 215, 0, 0.3);

  @media (max-width: 768px) {
    padding: ${theme.spacing.xl};
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.primary};
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: ${theme.colors.primary};
  font-size: ${theme.typography.fontSizes.xxl};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSizes.xl};
  }
`;

const StepContainer = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const StepTitle = styled.h3`
  color: ${theme.colors.primary};
  font-size: ${theme.typography.fontSizes.lg};
  margin: 0 0 ${theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSizes.md};
  }
`;

const StepList = styled.ol`
  color: ${theme.colors.textSecondary};
  line-height: 1.8;
  padding-left: ${theme.spacing.lg};
  margin: 0;

  li {
    margin-bottom: ${theme.spacing.sm};
    font-size: ${theme.typography.fontSizes.md};

    @media (max-width: 768px) {
      font-size: ${theme.typography.fontSizes.sm};
    }
  }
`;

const IconInstruction = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  background: rgba(255, 215, 0, 0.1);
  padding: 2px 8px;
  border-radius: ${theme.borderRadius.small};
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.primary};
`;

const CloseModalButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.md};
  background: ${theme.gradients.button};
  color: #000;
  border: none;
  border-radius: ${theme.borderRadius.medium};
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: ${theme.typography.fontSizes.md};
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: ${theme.spacing.lg};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 215, 0, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PWAInstallBanner = ({ position = 'top' }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [deviceType, setDeviceType] = useState('unknown'); // 'ios', 'android', 'desktop', 'unknown'

  useEffect(() => {
    // Check if already dismissed
    const dismissed = localStorage.getItem('pwaInstallDismissed');
    const installed = localStorage.getItem('pwaInstalled');
    
    if (dismissed || installed) {
      return;
    }

    // Detect device type
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setDeviceType('ios');
      // Check if already in standalone mode (installed)
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowBanner(true);
      }
    } else if (/android/i.test(userAgent)) {
      setDeviceType('android');
      // Android users will get the native prompt
      setShowBanner(true);
    } else {
      setDeviceType('desktop');
      setShowBanner(true);
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowBanner(false);
      localStorage.setItem('pwaInstalled', 'true');
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deviceType === 'ios') {
      // Show iOS specific instructions
      setShowInstructions(true);
    } else if (deferredPrompt) {
      // Android/Desktop with native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        localStorage.setItem('pwaInstalled', 'true');
        setShowBanner(false);
      }
      
      setDeferredPrompt(null);
    } else {
      // Fallback to instructions
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwaInstallDismissed', 'true');
    // Set expiry for 7 days
    setTimeout(() => {
      localStorage.removeItem('pwaInstallDismissed');
    }, 7 * 24 * 60 * 60 * 1000);
  };

  const renderIOSInstructions = () => (
    <StepContainer>
      <StepTitle>
        <FaApple /> iOS Installation
      </StepTitle>
      <StepList>
        <li>
          Tap the <IconInstruction><FaShareAlt /> Share</IconInstruction> button at the bottom of Safari
        </li>
        <li>
          Scroll down and tap <IconInstruction><FaPlus /> Add to Home Screen</IconInstruction>
        </li>
        <li>
          Tap <strong>"Add"</strong> in the top right corner
        </li>
        <li>
          The GG Trophy app icon will appear on your home screen
        </li>
      </StepList>
    </StepContainer>
  );

  const renderAndroidInstructions = () => (
    <StepContainer>
      <StepTitle>
        <FaAndroid /> Android Installation
      </StepTitle>
      <StepList>
        <li>
          Tap the <IconInstruction><FaEllipsisV /> Menu</IconInstruction> button (three dots) in your browser
        </li>
        <li>
          Select <IconInstruction><FaPlus /> Add to Home screen</IconInstruction> or <strong>"Install app"</strong>
        </li>
        <li>
          Confirm by tapping <strong>"Install"</strong> or <strong>"Add"</strong>
        </li>
        <li>
          The GG Trophy app will be installed on your device
        </li>
      </StepList>
    </StepContainer>
  );

  const renderDesktopInstructions = () => (
    <StepContainer>
      <StepTitle>
        <FaMobileAlt /> Desktop Installation
      </StepTitle>
      <StepList>
        <li>
          Look for the <IconInstruction><FaPlus /> Install</IconInstruction> icon in your browser's address bar
        </li>
        <li>
          Click the install icon or button
        </li>
        <li>
          Click <strong>"Install"</strong> in the confirmation dialog
        </li>
        <li>
          GG Trophy will open as a standalone app
        </li>
      </StepList>
    </StepContainer>
  );

  if (!showBanner) {
    return null;
  }

  return (
    <>
      <BannerContainer $position={position}>
        <BannerContent>
          <BannerText>
            <IconContainer>
              {deviceType === 'ios' && <FaApple />}
              {deviceType === 'android' && <FaAndroid />}
              {deviceType === 'desktop' && <FaMobileAlt />}
            </IconContainer>
            <TextContent>
              <BannerTitle>Install GG Trophy App</BannerTitle>
              <BannerDescription>
                Get the best experience! Install our app for quick access, offline support, and instant notifications.
              </BannerDescription>
            </TextContent>
          </BannerText>
          <ButtonGroup>
            <InstallButton onClick={handleInstall}>
              {deviceType === 'ios' || !deferredPrompt ? 'How to Install' : 'Install Now'}
            </InstallButton>
            <CloseButton onClick={handleDismiss} title="Dismiss">
              <FaTimes />
            </CloseButton>
          </ButtonGroup>
        </BannerContent>
      </BannerContainer>

      {showInstructions && (
        <InstructionsModal onClick={() => setShowInstructions(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <FaHome /> Install GG Trophy
              </ModalTitle>
              <CloseButton onClick={() => setShowInstructions(false)}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>

            {deviceType === 'ios' && renderIOSInstructions()}
            {deviceType === 'android' && renderAndroidInstructions()}
            {deviceType === 'desktop' && renderDesktopInstructions()}
            {deviceType === 'unknown' && (
              <>
                {renderIOSInstructions()}
                {renderAndroidInstructions()}
                {renderDesktopInstructions()}
              </>
            )}

            <CloseModalButton onClick={() => setShowInstructions(false)}>
              Got It!
            </CloseModalButton>
          </ModalContent>
        </InstructionsModal>
      )}
    </>
  );
};

export default PWAInstallBanner;
